import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import BaseReportPage from '../../../components/reports/BaseReportPage';
import BranchService from '../../../services/branchService';
import AuthService from '../../../services/authService';
import { formatUTCDate } from '../../../utils/dateUtils';

const SupplierReturnsFilters = ({ filters, onFiltersChange, onClear }) => {
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Get user role and branch
  const user = AuthService.getCurrentUser()
  const isManager = user?.role === 'manager'
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id
  
  // Initialize manager's branch filter on mount
  useEffect(() => {
    if (isManager && userBranchId && onFiltersChange) {
      const initialFilters = {
        ...filters,
        branch: userBranchId
      }
      console.log('🔧 Manager detected - Setting initial branch filter:', userBranchId)
      onFiltersChange(initialFilters)
    }
  }, [isManager, userBranchId]) // eslint-disable-line react-hooks/exhaustive-deps
  
  // Load branches from API
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoading(true)
        console.log('🔍 Supplier Returns Report: Loading branches')
        
        const branchResponse = await BranchService.getAllBranches()
        console.log('✅ Supplier Returns Report: Branches response:', branchResponse)
        
        let branchesData = []
        if (branchResponse && branchResponse.success) {
          if (branchResponse.data && branchResponse.data.branches && Array.isArray(branchResponse.data.branches)) {
            branchesData = branchResponse.data.branches
          } else if (Array.isArray(branchResponse.data)) {
            branchesData = branchResponse.data
          }
        }
        
        // Include all active branches (outlets and warehouses)
        const activeBranches = branchesData.filter(branch => branch.isActive)
        console.log('🏪 Supplier Returns Report: Active branches (outlets & warehouses):', activeBranches.length, 'branches')
        setBranches(activeBranches)
        
      } catch (error) {
        console.error('❌ Supplier Returns Report: Error loading branches:', error)
      } finally {
        setLoading(false)
      }
    }
    
    loadBranches()
  }, [])

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const handleClearFilters = () => {
    const clearedFilters = {
      branch: isManager ? userBranchId : ''
    }
    if (onClear) {
      onClear()
    }
    onFiltersChange(clearedFilters)
  }

  const hasActiveFilters = !isManager && filters?.branch

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >
            <span>Clear Filters</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
          <div className="relative">
            {isManager ? (
              // Manager: Read-only branch display
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-medium">
                {branches.find(b => b._id === userBranchId)?.name || 'My Branch'}
              </div>
            ) : (
              // Admin: Branch dropdown
              <>
                <select
                  value={filters?.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10 appearance-none"
                  disabled={loading}
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </>
            )}
          </div>
          {loading && !isManager && <p className="text-xs text-gray-500 mt-1">Loading branches...</p>}
        </div>
      </div>
    </div>
  )
}

const SupplierReturnsReport = () => {
  const columns = useMemo(() => [
    { 
      key: 'returnId', 
      title: 'Return ID', 
      sortable: true
    },
    { 
      key: 'purchaseOrderNumber', 
      title: 'PO Number', 
      sortable: true
    },
    { 
      key: 'supplierName', 
      title: 'Supplier', 
      sortable: true
    },
    { 
      key: 'branchName', 
      title: 'Branch', 
      sortable: true
    },
    { 
      key: 'itemName', 
      title: 'Item', 
      sortable: true
    },
    { 
      key: 'quantity', 
      title: 'Quantity', 
      sortable: true,
      render: (value) => {
        if (value === null || value === undefined) return '0'
        return parseFloat(value).toLocaleString()
      }
    },
    { 
      key: 'returnAmount', 
      title: 'Return Amount', 
      sortable: true,
      render: (value) => {
        if (value === null || value === undefined) return 'Rs. 0.00'
        const amount = parseFloat(value)
        return `Rs. ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
    },
    { 
      key: 'reason', 
      title: 'Return Reason', 
      sortable: false
    },
    { 
      key: 'returnDate', 
      title: 'Return Date', 
      sortable: true,
      render: (value) => {
        if (!value) return '-'
        return formatUTCDate(value, 'MMM dd, yyyy')
      }
    },
    { 
      key: 'processedBy', 
      title: 'Processed By', 
      sortable: true
    },
    { 
      key: 'status', 
      title: 'Status', 
      sortable: false,
      render: (value) => {
        const statusStyles = {
          'Pending': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
          'Processed': 'bg-green-100 text-green-800 border border-green-200',
          'Cancelled': 'bg-red-100 text-red-800 border border-red-200',
          'Approved': 'bg-blue-100 text-blue-800 border border-blue-200'
        }
        
        const className = statusStyles[value] || 'bg-gray-100 text-gray-800 border border-gray-200'
        
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>
            {value || 'Unknown'}
          </span>
        )
      }
    }
  ], [])

  return (
    <BaseReportPage
      title="Supplier Returns Report"
      subtitle="Track returns and refunds to suppliers"
      reportType="supplierReturns"
      columns={columns}
      enableFilters={true}
      customFilterComponent={SupplierReturnsFilters}
    />
  );
};

export default SupplierReturnsReport;